import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LanguageSwitcher from "@/components/feature/LanguageSwitcher";
import CategoryDropdown from "@/components/feature/CategoryDropdown";
import { MAIN_SITE_URL } from "@/mocks/categories";

const navLinks = [
  { key: "nav.categories", href: "/#categories" },
  { key: "nav.guides", href: "/#guides" },
  { key: "nav.faq", href: "/#faq" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24);
    }
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 w-full transition-all duration-300 border-b border-foreground-200/70 bg-background-50 ${
        scrolled ? "shadow-sm" : ""
      }`}
    >
      <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between px-4 py-3 md:px-6">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-grid-fill text-lg"></i>
          </span>
          <span className="font-heading text-base font-bold text-foreground-950">
            USFans Spreadsheet
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <Link
            to="/"
            className="rounded-full px-4 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:text-primary-700"
          >
            {t("nav.home")}
          </Link>
          {navLinks.map((l) => (
            <Link
              key={l.key}
              to={l.href}
              className="rounded-full px-4 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:text-primary-700"
            >
              {t(l.key)}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("nav.shopGuide")}
            <i className="ri-shopping-bag-line"></i>
          </a>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="flex items-center justify-center w-10 h-10 rounded-lg text-foreground-900 md:hidden"
          aria-label="Menu"
        >
          <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-foreground-200/60 bg-background-50 px-4 py-4 md:hidden">
          <nav className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-4-line"></i>
              </span>
              {t("nav.home")}
            </Link>

            <div className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              {t("nav.categories")}
            </div>
            <div className="px-4">
              <CategoryDropdown />
            </div>

            {navLinks.slice(1).map((l) => (
              <Link
                key={l.key}
                to={l.href}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line"></i>
                </span>
                {t(l.key)}
              </Link>
            ))}
          </nav>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-3 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50"
          >
            {t("nav.shopGuide")}
            <i className="ri-shopping-bag-line"></i>
          </a>

          <div className="mt-4 flex items-center justify-between px-1">
            <LanguageSwitcher />
          </div>
        </div>
      )}
    </header>
  );
}